import apiClient, { SYSTEM_TOKEN } from '@shared/api/client'
import { ENDPOINTS } from '@shared/api/endpoints'
import { groupSchedulesByDay } from './scheduleNormalizer'

/**
 * 護博會主頁「活動影音」的 event_id（用於 behindScenes list）
 */
const GUARDIAN_EXPO_HOME_VIDEO_EVENT_ID = 6

/**
 * 護博會子頁面（市集／講座／挑戰賽），對應 /v1/expo/media/list 的 expo_event
 */
export const EXPO_EVENT = {
  LECTURE: 1,
  CHALLENGE: 2,
  MARKET: 3,
}

/**
 * /v1/expo/media/list 的 media_type
 */
export const MEDIA_TYPE = {
  IMAGE: 1,
  VIDEO: 2,
}

// 儀表板（大螢幕看板）沒有登入態、可能同時開在多台裝置上長時間展示，
// 若沿用一般頁面的 viewer token 流程，每台裝置在無快取 token 時都會各自申請一組新的 viewer token，
// 長期會在後端堆積大量無用紀錄。這裡改用 SYSTEM_TOKEN 直接呼叫（跟 bannerService/monasteryService 同樣的既有作法），
// 只影響儀表板專用的 export，一般頁面（講座/市集/挑戰賽子頁面、護博會首頁）仍走原本的 viewer token 流程，不受影響。
const DASHBOARD_AUTH_HEADERS = { 'X-Auth-Token': SYSTEM_TOKEN }

/**
 * 取得挑戰賽賽程表，正規化為 ScheduleDayTabs 需要的 { year, month, date, weekday, entries } 格式
 * POST /v1/expo/challenge/list
 * @param {{ category?: string }} params
 */
export async function getChallengeSchedule(params = {}) {
  const res = await apiClient.post(ENDPOINTS.GUARDIAN_EXPO.CHALLENGE_LIST, params)
  return normalizeChallengeSchedule(res)
}

/**
 * 儀表板專用：取得挑戰賽賽程表（走 SYSTEM_TOKEN，不建立 viewer token）
 */
export async function getChallengeScheduleForDashboard(params = {}) {
  const res = await apiClient.post(ENDPOINTS.GUARDIAN_EXPO.CHALLENGE_LIST, params, { headers: DASHBOARD_AUTH_HEADERS })
  return normalizeChallengeSchedule(res)
}

function normalizeChallengeSchedule(res) {
  const allSchedules = (res.items ?? []).flatMap((item) =>
    (item.schedules ?? []).map((schedule) => ({ ...schedule, category: item.category }))
  )
  return groupSchedulesByDay(allSchedules)
}

/**
 * 取得挑戰賽總冠軍／亞軍／季軍名單
 * GET /v1/expo/challenge/rankList
 * @returns {Promise<{ category: string, winner: string }[]>} 依總冠軍／亞軍／季軍排序，缺少的名次略過
 */
export async function getChampionResults() {
  const res = await apiClient.get(ENDPOINTS.GUARDIAN_EXPO.CHALLENGE_RANK_LIST)
  return normalizeChampionResults(res)
}

/**
 * 儀表板專用：取得挑戰賽總冠軍／亞軍／季軍名單（走 SYSTEM_TOKEN，不建立 viewer token）
 */
export async function getChampionResultsForDashboard() {
  const res = await apiClient.get(ENDPOINTS.GUARDIAN_EXPO.CHALLENGE_RANK_LIST, { headers: DASHBOARD_AUTH_HEADERS })
  return normalizeChampionResults(res)
}

function normalizeChampionResults(res) {
  return (res.items ?? [])
    .filter((item) => item.monastery_name)
    .map((item) => ({ category: item.category, winner: item.monastery_name.replace(/^#/, '') }))
}

/**
 * 取得護博會主頁「活動影音」列表
 * POST /v1/event/behindScenes/list
 * event_id = 6（護博會主頁專屬，其餘子頁面之後會用各自的 API）
 */
export async function getGuardianExpoHomeVideoList(params = {}) {
  return apiClient.post(ENDPOINTS.BEHIND_SCENES.LIST, { event_id: GUARDIAN_EXPO_HOME_VIDEO_EVENT_ID, ...params })
}

/**
 * 取得護博會子頁面（市集／講座／挑戰賽）活動影音或活動圖片列表
 * POST /v1/expo/media/list
 * @param {{ expo_event: number, media_type: number }} params
 */
export async function getExpoMediaList(params) {
  const res = await apiClient.post(ENDPOINTS.GUARDIAN_EXPO.MEDIA_LIST, params)
  return res.items ?? []
}

export async function getChallengeRankList() {
  const res = await apiClient.get(ENDPOINTS.GUARDIAN_EXPO.CHALLENGE_RANK)
  return res.items ?? []
}

/**
 * 儀表板「博覽會資訊」的 notification option：0 全部（預設）／1 報到通知／2 其他公告
 */
export const NOTIFICATION_OPTION = {
  ALL: 0,
  CHECK_IN: 1,
  OTHER: 2,
}

/**
 * 儀表板專用：取得博覽會資訊（報到通知／其他公告）圖片列表（走 SYSTEM_TOKEN，不建立 viewer token）
 * POST /v1/expo/notification/list
 */
export async function getExpoNotificationListForDashboard(option = NOTIFICATION_OPTION.ALL) {
  const res = await apiClient.post(
    ENDPOINTS.GUARDIAN_EXPO.NOTIFICATION_LIST,
    { current_time: new Date().toString(), option },
    { headers: DASHBOARD_AUTH_HEADERS }
  )
  return (res.items ?? []).map((item) => item.url).filter(Boolean)
}
